import { VoyageDialogPopupManager } from "../../local_modules/voyage-dialog-popup/src/voyage-dialog-popup-manager";
import { constructEndedPayloadForEtaRequest, formatEtaDate, getParsedData } from "../../local_modules/voyage-eta-change-review/src/helper";
import { NotificationTypes, VoyNotification } from "../services/types/vessel-notifications-services.types";
import { VesselNotificationsService } from "../services/vessel-notifications-service";
import { VesselPlansService } from "../services/vessel-plans-service";
import { GetFormattedLocalDate } from "../utils/helpers";
import { IntermediatePlan, ScheduleChangeModelBase, ScheduleChangeStates, ScheduleEtaChangeType } from "./schedule-change-model";
import { VesselPlanningModel, VoyagePlan } from "./vessel-planning-model";
import audioAlert from "../../assets/audio/vessel-alert2.mp3"

export enum ScheduleChangeEvents {
    ReviewChange = "eta-change-review",
    RemindLater = "eta-remind-later",
    UserFlowEnd = "user-flow-end",
    DeclineAlert = "declined-alert",
    DisableEtaRequest = "disable-eta-request",
    EnableEtaRequest = "show-eta-request",
    GetLatestEtaRequest = "get-latest-eta-request",
    EnableEtaRequestButton = "enable-eta-request-button"
}

let currentScheduleChangeAlert: VoyNotification<NotificationTypes.ScheduledETAChange> | null = null;

export class ScheduleChangeModelImpl extends ScheduleChangeModelBase {
    private _shownEtaChangePopup = false;
    private _audio?: HTMLAudioElement;
    constructor() {
        super();
        this._initEtaFlow();
        setInterval(() => this._initEtaFlow(), 3 * 60 * 1000);
    }

    private async _initEtaFlow() {
        const alert = await this.getLatestAlert();

        if (!alert || Array.isArray(alert) && alert.length === 0){
            currentScheduleChangeAlert = null;
            this._shownEtaChangePopup = false;
            await this.checkForDeclinedRequest(alert);
            localStorage.removeItem('RemindLaterView');
            this.dispatchEvent(
                new CustomEvent(ScheduleChangeEvents.UserFlowEnd)
            );
            this.dispatchEvent(
                new CustomEvent(ScheduleChangeEvents.EnableEtaRequestButton, {
                    bubbles: true,
                    composed: true,
                })
            );
            return;
        };

        // Reset the flag if a new alert comes in
        if (!currentScheduleChangeAlert || currentScheduleChangeAlert.id !== alert.id) {
            this._shownEtaChangePopup = false;
        }
        
        currentScheduleChangeAlert = alert;
        if(alert.state == ScheduleChangeStates.Active || alert.state == ScheduleChangeStates.InProgress){
        this.dispatchEvent(
            new CustomEvent(ScheduleChangeEvents.DisableEtaRequest, {
                bubbles: true,
                composed: true,
            })
        )}
        await this.checkForDeclinedRequest(alert);
        await this.checkEtaChangeAlerts(alert);
    }

    async checkEtaChangeAlerts(alert : ScheduleEtaChangeType) {
        if (this._shownEtaChangePopup) return;

        if(alert.state === ScheduleChangeStates.Active){
            localStorage.removeItem('RemindLaterView');
            this.dispatchEvent(
                new CustomEvent(ScheduleChangeEvents.UserFlowEnd)
            );
        }

        const newArrivalEta = alert.data.schedule.arrivalTimings.array.find((item : any) => item.type === 'ESTIMATED')?.timestamp;

        const requestedEtaLocalTime = await formatEtaDate(newArrivalEta, alert.data.arrival_terminal_code);

        const reviewLaterView = localStorage.getItem('RemindLaterView');

        if(reviewLaterView || alert.state == ScheduleChangeStates.InProgress){
            this.dispatchEvent(
                new CustomEvent(ScheduleChangeEvents.RemindLater, {
                    detail: {
                        alertId: alert.id,
                        requestedEta: requestedEtaLocalTime,
                    },
                    bubbles: true,
                    composed: true,
                })
            );
            return;
        }

        if(alert.state == ScheduleChangeStates.Active){
            this._shownEtaChangePopup = true;
            if (!this._audio) {
                this._audio = new Audio(audioAlert);
            }

            try {
                await this._audio.play();
            } catch (err) {
                console.warn('Unable to play audio alert:', err);
            }

            const dialog = VoyageDialogPopupManager.createDialog({
                headerText: `ETA Change Request to ${requestedEtaLocalTime} LT`,
                bodyText: `You have a new ETA communication from TbM. Please click 
                on below to review the changes.`,
                acceptButtonText: 'Review changes',
                rejectButtonText: 'Remind me later',
                noCloseButton: true,
                noHeaderBox: true,
                subText: `Code: ${alert?.data.deviation_reason_code} | Details: ${alert?.data.deviation_reason}`
            });

            const response = await dialog.getResponse();

            const eventName = response === 'accept'
                ? ScheduleChangeEvents.ReviewChange
                : ScheduleChangeEvents.RemindLater;

            this.dispatchEvent(
                new CustomEvent(eventName, {
                    detail: {
                        alertId: alert.id,
                        requestedEta: requestedEtaLocalTime,
                    },
                    bubbles: true,
                    composed: true,
                })
            );
        }  
    }

    async getNewPlanWithUpdatedEta() : Promise< IntermediatePlan | null> {

        const planWithUpdatedEta = await VesselPlansService.getNewPlanWithUpdatedEta()
        if (!planWithUpdatedEta) {
            console.info('Plan not found with updated ETA to show ETA change review');
            return null;
        }

        return planWithUpdatedEta;
    }

    async updateNewPlanWithUpdatedEta(plan: VoyagePlan) : Promise<VoyagePlan | null> {

        const planWithUpdatedEta = await VesselPlansService.updatePlanWithUpdatedEta(plan);
        if (!planWithUpdatedEta) {
            console.info('Plan not found with updated ETA to show ETA change review');
            return null;
        }

        return planWithUpdatedEta;
    }

    async getLatestAlert(){
        const [activeAlerts, inProgressAlerts, declineAlert] = await Promise.all([
            VesselNotificationsService.fetchNotificationsByTypeAndState(NotificationTypes.ScheduledETAChange, ScheduleChangeStates.Active),
            VesselNotificationsService.fetchNotificationsByTypeAndState(NotificationTypes.ScheduledETAChange, ScheduleChangeStates.InProgress),
            VesselNotificationsService.fetchNotificationsByTypeAndState(NotificationTypes.ScheduledETAChange, ScheduleChangeStates.Declined)
        ]);

        // Combine both alerts
        const combinedAlerts = [...activeAlerts, ...inProgressAlerts, ...declineAlert];

        combinedAlerts.sort((a, b) => new Date(b.updatedAt ?? '').getTime() - new Date(a.updatedAt ?? '').getTime()); 

        const alert = combinedAlerts[0];
        return alert;
    }

    getCurrentAlert(){
        if(!currentScheduleChangeAlert)
            return null;

        return currentScheduleChangeAlert;
    }

    async checkForDeclinedRequest(alert: ScheduleEtaChangeType){
        if(!alert){
            this.dispatchEvent(
                new CustomEvent(ScheduleChangeEvents.DeclineAlert, {
                    detail: {
                        alert: null,
                    },  
                    bubbles: true,
                    composed: true,
                })
            );
            return;
        }
            
        if(alert.state == ScheduleChangeStates.Declined){
            this.dispatchEvent(
                new CustomEvent(ScheduleChangeEvents.DeclineAlert, {
                    detail: {
                        alert: alert,
                    },  
                    bubbles: true,
                    composed: true,
                })
            );
            return alert;
        } 
    }

    async checkIfOptimizationFailed(){
        //if the intermediate plan optimization failed in BE
       const alert = this.getCurrentAlert();

        if(!alert)
            return false

        if(alert.data.voyage_plan.bunker_consumption_mt == "0.00") //need to check the condition
            return true;
        else 
            return false;
    }

    async postStateChangeAlert(notificationId: string, state: string, data: any){
        const latestAlert = await VesselNotificationsService.fetchAlert(notificationId);

        const isEnded = latestAlert.state === ScheduleChangeStates.Ended;
        const isSameState = latestAlert.state === state;
        const isInprogress = state === ScheduleChangeStates.InProgress; //currently in progress
        const isAllowedInprogress = latestAlert.state === ScheduleChangeStates.Active; //only allowed to change the state to in-progress if the latest state is active
        const isAlreadyAccepted = latestAlert.state === ScheduleChangeStates.Accepted;
        const isTryingToDeclineAfterAccept = isAlreadyAccepted && state === ScheduleChangeStates.Declined;

        // Block Decline if already Accepted
        if (isTryingToDeclineAfterAccept) {
            console.warn("Decline not allowed after Accept.");
            return;
        }
        
        if (!isEnded && !isSameState) {
            if (!isInprogress || (isInprogress && isAllowedInprogress)) {
                await VesselNotificationsService.postScheduleChangeAlerts(notificationId, state, data);
            }
        }

        if(state == ScheduleChangeStates.Accepted) {
            this.dispatchEvent(
                new CustomEvent(ScheduleChangeEvents.DeclineAlert, {
                    detail: { alert: null },
                    bubbles: true,
                    composed: true,
                })
            );
        }
        
        if(state == ScheduleChangeStates.Accepted || state == ScheduleChangeStates.Declined){
            localStorage.removeItem('RemindLaterView');
            this.dispatchEvent(
                new CustomEvent(ScheduleChangeEvents.UserFlowEnd)
            );
        }
        await this._initEtaFlow(); //update latest notification
    }

    async postEtaRequestAlert(data: any){
        // end the last request if exists
        const existingAlert = await this.getEtaRequestAlert();
        if(existingAlert){
            const existingAlertData = getParsedData(existingAlert.data);
            const endedPayload = constructEndedPayloadForEtaRequest(existingAlertData);
            await VesselNotificationsService.postScheduleChangeAlerts(existingAlert.id, ScheduleChangeStates.Ended, endedPayload);
        }
        //create new ETA request notification
        await VesselNotificationsService.createEtaRequestNotification(data);

        //fetch latest notification
        this.dispatchEvent(new CustomEvent(ScheduleChangeEvents.GetLatestEtaRequest, {bubbles: true, composed: true}));
    }

    async getEtaRequestAlert() {
        const alerts = await VesselNotificationsService.fetchActiveNotifications(NotificationTypes.RequestETAChange);
        if (alerts.length === 0) {
            return null;
        }
        return alerts[0];
    }
}
